// Single SBA 7(a) acquisition model — powers the SBA calculator AND the listing
// page's debt-service estimate. Never re-derive this math elsewhere: a second
// copy with its own constants is exactly how the two used to disagree.

// ── Program guardrails ───────────────────────────────────────────────────────
export const SBA_MAX_LOAN = 5_000_000;
/** Minimum equity injection, as a % of total project cost. */
export const SBA_MIN_EQUITY_PCT = 10;
/** A full-standby seller note may cover at most this share of the required injection. */
export const SBA_SELLER_NOTE_EQUITY_SHARE = 0.5;
export const SBA_MIN_DSCR = 1.25;
/** Max amortization without real estate in the deal (business acquisition). */
export const SBA_MAX_TERM_NO_RE = 10;
export const SBA_MAX_TERM_WITH_RE = 25;

/**
 * Upfront SBA guaranty fee, on the guaranteed portion of the loan. This is the
 * standard statutory schedule; the SBA waives or adjusts it by fiscal year, so
 * it's an estimate — the disclaimer tells buyers to confirm with a lender.
 */
function guarantyFee(baseLoan: number): { guaranteedPct: number; fee: number } {
  if (baseLoan <= 0) return { guaranteedPct: 0, fee: 0 };
  const guaranteedPct = baseLoan <= 150_000 ? 85 : 75;
  const guaranteed = baseLoan * (guaranteedPct / 100);
  let fee: number;
  if (baseLoan <= 150_000) fee = guaranteed * 0.02;
  else if (baseLoan <= 700_000) fee = guaranteed * 0.03;
  else fee = Math.min(guaranteed, 1_000_000) * 0.035 + Math.max(0, guaranteed - 1_000_000) * 0.0375;
  return { guaranteedPct, fee };
}

export type CashFlowBasis = "sde" | "ebitda";

export interface SbaInputs {
  askingPrice: number;
  /** Closing costs (legal, appraisal, packaging) as a % of price — financed into the project. */
  closingCostsPct: number;
  /** Working capital financed with the deal. */
  workingCapital: number;
  /** Buyer cash equity, as a % of total project cost — the base the SBA's 10% rule uses. */
  downPaymentPct: number;
  /** Seller note, as a % of asking price. */
  sellerNotePct: number;
  sellerNoteRate: number;
  sellerNoteTermYears: number;
  /** Full standby: no payments while the SBA loan is outstanding, so it can count toward equity. */
  sellerNoteStandby: boolean;
  interestRate: number;
  loanTermYears: number;
  includesRealEstate: boolean;
  /** SDE still includes the owner's pay; EBITDA already has it deducted. */
  cashFlowBasis: CashFlowBasis;
  cashFlow: number;
  /** Salary for whoever runs the business — subtracted from SDE only. */
  ownerSalary: number;
  /** Annual payments on any debt that survives closing. */
  otherDebtPayments: number;
}

export const SBA_DEFAULTS: SbaInputs = {
  askingPrice: 500_000,
  closingCostsPct: 3,
  workingCapital: 0,
  downPaymentPct: 10,
  sellerNotePct: 0,
  sellerNoteRate: 6,
  sellerNoteTermYears: 5,
  sellerNoteStandby: true,
  interestRate: 11.5,
  loanTermYears: 10,
  includesRealEstate: false,
  cashFlowBasis: "sde",
  cashFlow: 200_000,
  ownerSalary: 60_000,
  otherDebtPayments: 0,
};

/** Level monthly payment on a fully amortizing loan. */
export function monthlyPayment(principal: number, ratePct: number, years: number): number {
  if (principal <= 0) return 0;
  const n = years * 12;
  const r = ratePct / 100 / 12;
  return r === 0 ? principal / n : (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export type SbaStatus = "eligible" | "review" | "no-loan";

export interface SbaResult {
  askingPrice: number;
  closingCosts: number;
  workingCapital: number;
  totalProjectCost: number;
  downPayment: number;
  sellerNote: number;
  baseLoan: number;
  guaranteedPct: number;
  guarantyFee: number;
  /** Base loan + financed guaranty fee. */
  loanAmount: number;
  monthlyPayment: number;
  annualDebtService: number;
  sellerNoteAnnual: number;
  /** Seller-note payments counted in DSCR (0 while on standby). */
  sellerNoteAnnualInDscr: number;
  otherDebtPayments: number;
  totalAnnualDebtService: number;
  adjustedCashFlow: number;
  /** null when there's no debt to cover. */
  dscr: number | null;
  equityCounted: number;
  equityPct: number;
  buyerCashRequired: number;
  /** Buyer cash needed to hit the minimum injection on its own terms (after any eligible seller note). */
  minBuyerCash: number;
  status: SbaStatus;
  reasons: string[];
}

export function computeSba(i: SbaInputs): SbaResult {
  const closingCosts = i.askingPrice * (i.closingCostsPct / 100);
  const totalProjectCost = i.askingPrice + closingCosts + i.workingCapital;
  const downPayment = totalProjectCost * (i.downPaymentPct / 100);
  const sellerNote = i.askingPrice * (i.sellerNotePct / 100);

  const baseLoan = Math.max(0, totalProjectCost - downPayment - sellerNote);
  const { guaranteedPct, fee } = guarantyFee(baseLoan);
  const loanAmount = baseLoan + fee;

  const monthly = monthlyPayment(loanAmount, i.interestRate, i.loanTermYears);
  const annualDebtService = monthly * 12;
  const sellerNoteAnnual = monthlyPayment(sellerNote, i.sellerNoteRate, i.sellerNoteTermYears) * 12;
  const sellerNoteAnnualInDscr = i.sellerNoteStandby ? 0 : sellerNoteAnnual;
  const totalAnnualDebtService = annualDebtService + sellerNoteAnnualInDscr + i.otherDebtPayments;

  const adjustedCashFlow = i.cashFlowBasis === "sde" ? i.cashFlow - i.ownerSalary : i.cashFlow;
  const dscr = totalAnnualDebtService > 0 ? adjustedCashFlow / totalAnnualDebtService : null;

  const requiredEquity = totalProjectCost * (SBA_MIN_EQUITY_PCT / 100);
  const noteEquity = i.sellerNoteStandby ? Math.min(sellerNote, requiredEquity * SBA_SELLER_NOTE_EQUITY_SHARE) : 0;
  const equityCounted = downPayment + noteEquity;
  const equityPct = totalProjectCost > 0 ? (equityCounted / totalProjectCost) * 100 : 0;
  const minBuyerCash = Math.max(0, requiredEquity - noteEquity);

  const reasons: string[] = [];
  let status: SbaStatus;
  if (loanAmount === 0) {
    status = "no-loan";
    reasons.push("No SBA loan needed — buyer cash and the seller note cover the full project cost");
  } else {
    if (loanAmount > SBA_MAX_LOAN)
      reasons.push(`Loan exceeds the $${SBA_MAX_LOAN / 1_000_000}M SBA 7(a) maximum`);
    if (equityCounted + 0.005 < requiredEquity)
      reasons.push(
        `Equity injection is ${equityPct.toFixed(1)}% of project cost — ${SBA_MIN_EQUITY_PCT}% required` +
          (i.sellerNoteStandby ? "" : " (a seller note on full standby can cover up to half)")
      );
    if (dscr !== null && dscr < SBA_MIN_DSCR)
      reasons.push(`DSCR below the ${SBA_MIN_DSCR.toFixed(2)} lender threshold`);
    if (i.loanTermYears > SBA_MAX_TERM_NO_RE && !i.includesRealEstate)
      reasons.push(`Terms over ${SBA_MAX_TERM_NO_RE} years require real estate in the deal`);
    status = reasons.length === 0 ? "eligible" : "review";
  }

  return {
    askingPrice: i.askingPrice,
    closingCosts,
    workingCapital: i.workingCapital,
    totalProjectCost,
    downPayment,
    sellerNote,
    baseLoan,
    guaranteedPct,
    guarantyFee: fee,
    loanAmount,
    monthlyPayment: monthly,
    annualDebtService,
    sellerNoteAnnual,
    sellerNoteAnnualInDscr,
    otherDebtPayments: i.otherDebtPayments,
    totalAnnualDebtService,
    adjustedCashFlow,
    dscr,
    equityCounted,
    equityPct,
    buyerCashRequired: downPayment,
    minBuyerCash,
    status,
    reasons,
  };
}

/**
 * Highest asking price at which the deal still clears `target` DSCR, holding
 * every other input (percentages, cash flow, terms) fixed. DSCR falls as price
 * rises, so a bisection is exact enough. null = no price works (cash flow can't
 * cover the other debt) or there's no loan to cover.
 */
export function maxPriceForDscr(i: SbaInputs, target = SBA_MIN_DSCR): number | null {
  const dscrAt = (askingPrice: number) => computeSba({ ...i, askingPrice }).dscr;
  const floor = dscrAt(1);
  if (floor === null || floor < target) return null;

  let lo = 1;
  let hi = Math.max(i.askingPrice, 1_000);
  // Grow the bracket until DSCR drops below target (bounded — prices past $1B are noise).
  while ((dscrAt(hi) ?? Infinity) >= target) {
    lo = hi;
    hi *= 2;
    if (hi > 1_000_000_000) return null;
  }
  for (let k = 0; k < 60 && hi - lo > 100; k++) {
    const mid = (lo + hi) / 2;
    if ((dscrAt(mid) ?? Infinity) >= target) lo = mid;
    else hi = mid;
  }
  return Math.floor(lo / 1_000) * 1_000;
}

export interface AmortizationYear {
  year: number;
  payment: number;
  interest: number;
  principal: number;
  endingBalance: number;
}

/** Year-by-year rollup of the monthly amortization. */
export function amortizationSchedule(principal: number, ratePct: number, years: number): AmortizationYear[] {
  const pmt = monthlyPayment(principal, ratePct, years);
  const r = ratePct / 100 / 12;
  const rows: AmortizationYear[] = [];
  let balance = principal;
  for (let y = 1; y <= years; y++) {
    let interest = 0;
    let paid = 0;
    for (let m = 0; m < 12; m++) {
      const int = balance * r;
      const prin = Math.min(balance, pmt - int);
      interest += int;
      paid += int + prin;
      balance -= prin;
    }
    rows.push({ year: y, payment: paid, interest, principal: paid - interest, endingBalance: Math.max(0, balance) });
  }
  return rows;
}

// ── Scenario ↔ query string (the calculator's "Copy link" share) ─────────────
const BOOL_KEYS = ["sellerNoteStandby", "includesRealEstate"] as const;

export function toSbaQuery(i: SbaInputs): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(i)) q.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
  return q.toString();
}

/** Lenient: unknown or malformed params are ignored, so a stale link still loads. */
export function parseSbaQuery(search: string): Partial<SbaInputs> {
  const q = new URLSearchParams(search);
  const out: Record<string, unknown> = {};
  for (const [k, def] of Object.entries(SBA_DEFAULTS)) {
    const v = q.get(k)?.trim();
    if (!v) continue;
    if (typeof def === "number" && Number.isFinite(Number(v))) out[k] = Number(v);
  }
  for (const k of BOOL_KEYS) {
    const v = q.get(k);
    if (v === "1" || v === "0") out[k] = v === "1";
  }
  const basis = q.get("cashFlowBasis");
  if (basis === "sde" || basis === "ebitda") out.cashFlowBasis = basis;
  return out as Partial<SbaInputs>;
}
